import type { AnchorHTMLAttributes, FunctionComponent } from 'react';
import {
  BadgeVariant,
  spacingHelper,
  type LinkDangerouslySetClassNames,
  type LinkProps,
} from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { Badge } from '@property.com.au/ui-kit';
import { ExternalLink } from '../ExternalLink/ExternalLink';
import { IconInfo } from '../types';
import { OutboundTrackingEventData } from '../../../analytics/event-tracking/trackOutboundLinkClick';
import { ClickThroughSourceLocalStorageValue } from '../../../analytics/click-through-tracking/TrackingSource';
import { InternalLink } from '../InternalLink/InternalLink';

const StyledBadge = styled(Badge)`
  margin-left: ${spacingHelper('extraSmall')};
  align-self: center;
  height: ${spacingHelper('medium')};
`;

interface Callout {
  text: string;
  variant: BadgeVariant;
}

export interface WebLinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'rel' | 'className'> {
  title: string;
  url: string;
  isExternal: boolean;
  variant: LinkProps['variant'];
  iconInfo?: IconInfo | null;
  callout?: Callout | null;
  tracking?: {
    outboundTracking?: OutboundTrackingEventData | null;
    clickThroughTracking?: ClickThroughSourceLocalStorageValue | null;
  } | null;
  key?: string;
  className?: string;
  sizeVariant?: LinkProps['sizeVariant'];
  dangerouslySetClassNames?: LinkDangerouslySetClassNames;
  rel?: string | null;
  onClick?: () => void;
  fullWidth?: boolean;
  disabled?: boolean | null;
  clientSideRouting?: boolean;
}

export const WebLink: FunctionComponent<WebLinkProps> = ({
  title,
  isExternal,
  url,
  variant,
  tracking,
  rel,
  callout,
  disabled,
  ...rest
}) => {
  return isExternal ? (
    <ExternalLink
      key={url}
      href={url}
      linkVariant={variant}
      {...(rel && { rel })}
      outboundLinkType={tracking?.outboundTracking?.data.type || ''}
      schema={tracking?.outboundTracking?.schema}
      event_name={tracking?.outboundTracking?.data.event_name}
      disabled={disabled ?? undefined}
      {...rest}
    >
      {title}
    </ExternalLink>
  ) : (
    <InternalLink
      key={url}
      to={url}
      linkVariant={variant}
      clickThroughTracking={tracking?.clickThroughTracking}
      rel={rel || undefined}
      disabled={disabled ?? undefined}
      {...rest}
    >
      {title}
      {callout && <StyledBadge variant={callout.variant}>{callout.text}</StyledBadge>}
    </InternalLink>
  );
};
